<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import Sortable from 'sortablejs';

interface Props {
   
  modelValue?: any[];
  itemKey?: string;
  handle?: string;
  group?: string | { name: string; pull?: boolean; put?: boolean };
  disabled?: boolean;
  scrollableContainer?: string; // CSS selector for scrollable container
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  itemKey: 'id',
  handle: '.drag-handle',
  disabled: false
});

const emit = defineEmits<{
   
  'update:modelValue': [value: any[]];
   
  'change': [event: { added?: { newIndex: number; element: any }; removed?: { oldIndex: number; element: any }; moved?: { newIndex: number; oldIndex: number; element: any } }];
}>();

const container = ref<HTMLElement>();
let sortable: Sortable | null = null;
/**
 * Bumped synchronously every time we kick off an init or destroy. After the `await nextTick()`
 * inside {@link initSortable}, the resumed code checks that no newer init/destroy was scheduled
 * — if one was, this one aborts. Prevents two concurrent `Sortable.create()` calls from binding
 * to the same DOM element, which corrupts sortablejs's global `sortables` array and produces
 * `TypeError: null is not an object (evaluating 'sortable[expando].options')` on the next
 * dragover (TABASCO-1HK).
 */
let initGeneration = 0;

 
const getItemKey = (item: any, index: number): string | number => {
  if (typeof item === 'object' && item !== null && props.itemKey in item) {
    const key = item[props.itemKey];
    return typeof key === 'string' || typeof key === 'number' ? key : index;
  }
  return index;
};

const initSortable = async () => {
  if (!container.value) return;
  const generationAtStart = ++initGeneration;

  await nextTick();

  // Another init/destroy ran while we were waiting — abandon this attempt so we don't
  // double-bind on the same DOM element.
  if (generationAtStart !== initGeneration || !container.value) return;

  // A prior instance may still be bound to this container from a synchronous re-entry
  // (e.g. handle/group prop change). Tear it down first so sortablejs's `sortables`
  // global only holds the current binding for this element.
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }

  // Find the scrollable container if specified
  let scrollContainer: HTMLElement | boolean = true;
  if (props.scrollableContainer) {
    const element = document.querySelector(props.scrollableContainer) as HTMLElement;
    if (element) {
      scrollContainer = element;
    }
  }
  
  const prefersCoarsePointer =
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  sortable = Sortable.create(container.value, {
    animation: 150,
    handle: props.handle,
    group: props.group,
    disabled: props.disabled,
    scroll: scrollContainer, // Enable scrolling while dragging
    scrollSensitivity: 60, // Distance from edge to start scrolling
    scrollSpeed: 10, // Scroll speed
    bubbleScroll: true, // Enable scrolling in nested containers
    // Fallback drag (mousemove-driven) behaves better on coarse pointers; fine pointers use native
    // HTML5 DnD so Playwright `dragTo` and real mice work in headless and desktop environments.
    forceFallback: prefersCoarsePointer,
    onEnd: (evt) => {
      if (evt.oldIndex !== evt.newIndex && evt.oldIndex !== undefined && evt.newIndex !== undefined) {
        const newList = [...props.modelValue];
        const [removed] = newList.splice(evt.oldIndex, 1);
        newList.splice(evt.newIndex, 0, removed);
        
        emit('update:modelValue', newList);
        emit('change', {
          removed: { oldIndex: evt.oldIndex, element: removed }
        });
      }
    }
  });
};

const destroySortable = () => {
  // Bump so any queued initSortable awaiting nextTick aborts instead of binding to a stale container.
  initGeneration++;
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
};

onMounted(() => {
  initSortable();
});

onUnmounted(() => {
  destroySortable();
});

watch(() => props.disabled, (newVal) => {
  if (sortable) {
    sortable.option('disabled', newVal);
  }
});

watch(() => props.handle, () => {
  destroySortable();
  initSortable();
});

watch(() => props.group, () => {
  destroySortable();
  initSortable();
});

watch(() => props.scrollableContainer, () => {
  destroySortable();
  initSortable();
});

// Intentionally no `watch(() => props.modelValue, ...)`. Sortable reads child positions from
// the live DOM at drag time, and Vue's keyed v-for keeps children stable across reorders, so
// re-binding on every modelValue change is unnecessary — and the previous destroy/recreate
// pattern races with sortablejs's global `sortables` array, producing TABASCO-1HK on the next
// dragover after a column/filter-rule update.
</script> 

<template>
  <div ref="container" class="draggable-container">
    <div v-for="(item, index) in modelValue" :key="getItemKey(item, index)" class="draggable-item">
      <slot name="item" :element="item" :index="index" />
    </div>
  </div>
</template>
